import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedBackground from '../AnimatedBackground/AnimatedBackground';
import './Services.css';
import { AutomationIcon, AIAnalyticsIcon, IoTIcon, MaintenanceIcon, MonitoringIcon, MESIcon } from '../Icons/AnimatedIcons';
import { ParticleBackground } from '../Background/AnimatedBackground';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: <AutomationIcon />,
      title: 'Industrial Automation',
      description: 'Complete automation solutions for manufacturing excellence, from concept to commissioning.',
      features: ['PLC Programming', 'SCADA Systems', 'Process Control', 'Factory Automation'],
      image: process.env.PUBLIC_URL + '/images/IA.jpg'
    },
    {
      icon: <AIAnalyticsIcon />,
      title: 'AI Data Analytics',
      description: 'Transform raw data into actionable insights with machine learning and advanced analytics.',
      features: ['Predictive Analytics', 'Pattern Recognition', 'Real-time Processing', 'Custom Dashboards'],
      image: process.env.PUBLIC_URL + '/images/BG_AI.png'
    },
    {
      icon: <IoTIcon />,
      title: 'IoT Solutions',
      description: 'Connect, monitor, and optimize your operations with intelligent IoT ecosystems.',
      features: ['Smart Sensors', 'Edge Computing', 'Cloud Integration', 'Device Management'],
      image: process.env.PUBLIC_URL + '/images/BG_IoT.png'
    },
    {
      icon: <MaintenanceIcon />,
      title: 'Predictive Maintenance',
      description: 'Minimize downtime and maximize efficiency with AI-powered maintenance predictions.',
      features: ['Asset Monitoring', 'Failure Prediction', 'Maintenance Scheduling', 'Cost Optimization'],
      image: process.env.PUBLIC_URL + '/images/BP_PdTM.png'
    },
    {
      icon: <MonitoringIcon />,
      title: 'Remote Monitoring',
      description: '24/7 real-time monitoring and alert systems for complete operational visibility.',
      features: ['Live Dashboards', 'Alert Systems', 'Mobile Access', 'Historical Analysis'],
      image: process.env.PUBLIC_URL + '/images/BG_Remote.png'
    },
    {
      icon: <MESIcon />,
      title: 'Custom MES Applications',
      description: 'Tailored Manufacturing Execution Systems for seamless production management.',
      features: ['Production Tracking', 'Quality Control', 'Resource Planning', 'Compliance Management'],
      image: process.env.PUBLIC_URL + '/images/BG_MES.png'
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
        ease: [0.25, 0.4, 0.25, 1],
      },
    }),
  };

  return (
    <section id="services" className="services" ref={ref}>

      <div className="container">
        <div className="services-content-wrapper">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-title">Comprehensive Solutions</h2>
            <p className="section-subtitle">
              Full-spectrum services designed to transform your industrial operations
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                custom={index}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
                variants={cardVariants}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <div className="service-card-content">
                  {service.image && (
                    <img src={service.image} alt={service.title} className="service-card-image" />
                  )}
                  <div className={`service-icon icon-${service.icon}`}></div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>
                        <span className="feature-bullet">→</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
